/**
 * Environment API Client
 *
 * Handles all environment-related API calls.
 * Backend wraps responses in { success: true, data: T } — we unwrap here.
 */

import { AppConfig } from '../auth/config';

export interface ApiEnvironment {
  id: string;
  name: string;
  isGlobal: boolean;
  variables?: ApiVariable[];
  createdAt?: string;
  updatedAt?: string;
}

export interface ApiVariable {
  id?: string;
  key: string;
  value: string;
  isSecret: boolean;
  enabled?: boolean;
}

export interface CreateEnvironmentRequest {
  name: string;
  isGlobal?: boolean;
  variables?: Array<{ key: string; value: string; isSecret?: boolean; enabled?: boolean }>;
}

export interface UpdateEnvironmentRequest {
  name?: string;
  isGlobal?: boolean;
  variables?: Array<{ key: string; value: string; isSecret?: boolean; enabled?: boolean }>;
}

/**
 * Create the environment API client
 */
export function createEnvironmentApi(config: AppConfig | null, getAccessToken: () => Promise<string | null>) {
  const baseUrl = config?.api?.endpoint || '';

  async function fetchWithAuth<T>(path: string, options: RequestInit = {}): Promise<T> {
    const token = await getAccessToken();
    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${baseUrl}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(error.error?.message || error.message || `Request failed: ${response.status}`);
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return null as T;
    }

    const result = await response.json();
    // Unwrap the { success: true, data: T } envelope
    return result.data !== undefined ? result.data : result;
  }

  return {
    async listEnvironments(workspaceId: string): Promise<ApiEnvironment[]> {
      return fetchWithAuth(`/workspaces/${workspaceId}/environments`);
    },

    async getEnvironment(id: string): Promise<ApiEnvironment> {
      return fetchWithAuth(`/environments/${id}`);
    },

    async createEnvironment(workspaceId: string, data: CreateEnvironmentRequest): Promise<ApiEnvironment> {
      return fetchWithAuth(`/workspaces/${workspaceId}/environments`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    async updateEnvironment(id: string, data: UpdateEnvironmentRequest): Promise<ApiEnvironment> {
      return fetchWithAuth(`/environments/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
    },

    async deleteEnvironment(id: string): Promise<void> {
      await fetchWithAuth(`/environments/${id}`, { method: 'DELETE' });
    },
  };
}

export type EnvironmentApi = ReturnType<typeof createEnvironmentApi>;
